import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorService } from '../doctorService';

export function useDoctors(
  page = 1,
  limit = 20,
  filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
) {
  return useQuery({
    queryKey: ['doctors', page, limit, filters],
    queryFn: () => doctorService.getDoctors(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useDoctor(id: string) {
  return useQuery({
    queryKey: ['doctors', id],
    queryFn: () => doctorService.getDoctor(id),
    enabled: !!id,
  });
}

export function useCreateDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: doctorService.createDoctor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => doctorService.updateDoctor(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['doctors', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      // Doctor edits sync name/roles onto the linked user account.
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => doctorService.deleteDoctor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
    },
  });
}

/** Bulk-delete doctors as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDoctors() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await doctorService.deleteDoctor(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
    },
  });
}

export function useTrashedDoctors(
  page = 1,
  limit = 20,
  filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
) {
  return useQuery({
    queryKey: ['doctors', 'trash', page, limit, filters],
    queryFn: () => doctorService.getTrashedDoctors(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useRestoreDoctors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => doctorService.restoreDoctors(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function usePermanentlyDeleteDoctors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => doctorService.permanentlyDeleteDoctors(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
    },
  });
}

/** Force-delete trashed doctors together with every linked record. */
export function useForceDeleteDoctors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => doctorService.forceDeleteDoctors(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
